import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Search, Trash2, AlertTriangle, History, Loader2, ShieldAlert, Package, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { searchOrdersForPurge, purgeOrder, getDeletionHistory } from '../../lib/api';

export default function SalesOrderPurge() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);

  const [confirmDialog, setConfirmDialog] = useState(null); // { dispatch }
  const [reason, setReason] = useState('');
  const [purging, setPurging] = useState(false);

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeTab, setActiveTab] = useState('search');

  const handleSearch = useCallback(async () => {
    if (!searchQuery || searchQuery.length < 2) return;
    setSearching(true);
    try {
      const { data } = await searchOrdersForPurge(searchQuery);
      setResults(data.results || []);
    } catch {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const { data } = await getDeletionHistory();
      setHistory(data.records || []);
    } catch {
      toast.error('Failed to load deletion history');
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'history') loadHistory();
  }, [activeTab, loadHistory]);

  const handlePurge = async () => {
    if (!confirmDialog || !reason.trim() || reason.trim().length < 5) {
      toast.error('Please provide a reason (min 5 characters)');
      return;
    }
    setPurging(true);
    try {
      const { data } = await purgeOrder(confirmDialog.id, reason.trim());
      const parts = [`Order ${confirmDialog.dispatch_number} purged`];
      if (data.batches_deleted > 0) parts.push(`${data.batches_deleted} batch(es)`);
      if (data.transformations_deleted > 0) parts.push(`${data.transformations_deleted} transformation(s)`);
      toast.success(parts.join(' + ') + ' deleted');
      setConfirmDialog(null);
      setReason('');
      setResults(prev => prev.filter(r => r.id !== confirmDialog.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try {
      const date = new Date(d);
      if (isNaN(date.getTime())) return '—';
      return date.toLocaleDateString('en-IE', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return '—'; }
  };

  const statusColor = (s) => {
    if (s === 'dispatched') return 'bg-green-100 text-green-800';
    if (s === 'ready') return 'bg-blue-100 text-blue-800';
    return 'bg-zinc-100 text-zinc-700';
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/data-management')} data-testid="btn-back-to-dm">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-red-600" /> Sales Order Purge
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Search and soft-delete errant sales orders with full compliance audit trail</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="search" data-testid="tab-purge-search">
              <Search className="w-4 h-4 mr-1.5" /> Search & Purge
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-purge-history">
              <History className="w-4 h-4 mr-1.5" /> Deletion History
            </TabsTrigger>
          </TabsList>

          {/* Search & Purge Tab */}
          <TabsContent value="search">
            <div className="flex gap-2 mb-4">
              <Input
                placeholder="Search by customer name, PO number, or order number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1"
                data-testid="input-purge-search"
              />
              <Button onClick={handleSearch} disabled={searching || searchQuery.length < 2} data-testid="btn-purge-search">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                <span className="ml-1.5">Search</span>
              </Button>
            </div>

            {results.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40">
                      <TableHead className="font-semibold">Order #</TableHead>
                      <TableHead className="font-semibold">Customer</TableHead>
                      <TableHead className="font-semibold">PO Number</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold text-center">Lines</TableHead>
                      <TableHead className="font-semibold text-center">Batches</TableHead>
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map((order) => (
                      <TableRow key={order.id} data-testid={`purge-result-${order.dispatch_number}`}>
                        <TableCell className="font-mono font-medium">{order.dispatch_number}</TableCell>
                        <TableCell>{order.customer_name}</TableCell>
                        <TableCell className="font-mono text-sm">{order.po_number || '—'}</TableCell>
                        <TableCell>
                          <Badge className={statusColor(order.status)} variant="secondary">{order.status}</Badge>
                        </TableCell>
                        <TableCell className="text-center">{order.lines_count}</TableCell>
                        <TableCell className="text-center">
                          {order.linked_batches > 0 ? (
                            <Badge variant="outline" className="text-amber-700 border-amber-300">
                              <Package className="w-3 h-3 mr-1" />{order.linked_batches}
                            </Badge>
                          ) : '0'}
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(order.dispatch_date || order.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => { setConfirmDialog(order); setReason(''); }}
                            data-testid={`btn-purge-${order.dispatch_number}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Purge
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {results.length === 0 && searchQuery.length >= 2 && !searching && (
              <div className="text-center py-8 text-muted-foreground">
                No orders found matching "{searchQuery}"
              </div>
            )}
          </TabsContent>

          {/* Deletion History Tab */}
          <TabsContent value="history">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading history...
              </div>
            ) : history.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/40">
                      <TableHead className="font-semibold">Order #</TableHead>
                      <TableHead className="font-semibold">Customer</TableHead>
                      <TableHead className="font-semibold">PO</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold text-center">Batches</TableHead>
                      <TableHead className="font-semibold text-center">Trans.</TableHead>
                      <TableHead className="font-semibold">Reason</TableHead>
                      <TableHead className="font-semibold">Deleted By</TableHead>
                      <TableHead className="font-semibold">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((rec) => (
                      <TableRow key={rec.id} className="bg-red-50/30">
                        <TableCell className="font-mono font-medium">{rec.dispatch_number}</TableCell>
                        <TableCell>{rec.customer_name}</TableCell>
                        <TableCell className="font-mono text-sm">{rec.po_number || '—'}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="bg-zinc-100">{rec.status_at_deletion}</Badge>
                        </TableCell>
                        <TableCell className="text-center">{rec.batches_deleted}</TableCell>
                        <TableCell className="text-center">{rec.transformations_deleted || 0}</TableCell>
                        <TableCell className="max-w-[250px]">
                          <p className="text-sm truncate" title={rec.reason}>{rec.reason}</p>
                        </TableCell>
                        <TableCell className="text-sm">{rec.deleted_by}</TableCell>
                        <TableCell className="text-sm">{formatDate(rec.deleted_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No deletion history found
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Confirmation Dialog */}
        <Dialog open={!!confirmDialog} onOpenChange={(open) => !open && setConfirmDialog(null)}>
          <DialogContent className="sm:max-w-[520px]" data-testid="purge-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Confirm Order Purge
              </DialogTitle>
              <DialogDescription>
                This will permanently soft-delete the sales order and <strong>all associated data</strong>. Records are retained for audit.
              </DialogDescription>
            </DialogHeader>

            {confirmDialog && (
              <div className="space-y-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1 text-sm">
                  <p><span className="font-semibold">Order:</span> {confirmDialog.dispatch_number}</p>
                  <p><span className="font-semibold">Customer:</span> {confirmDialog.customer_name}</p>
                  <p><span className="font-semibold">PO:</span> {confirmDialog.po_number || '—'}</p>
                  <p><span className="font-semibold">Status:</span> {confirmDialog.status}</p>
                </div>

                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-900 space-y-1.5">
                  <p className="font-semibold flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                    The following will be deleted:
                  </p>
                  <ul className="list-disc list-inside ml-1 space-y-0.5">
                    <li>The sales order ({confirmDialog.lines_count} line item{confirmDialog.lines_count !== 1 ? 's' : ''})</li>
                    {confirmDialog.linked_batches > 0 && (
                      <li><strong>{confirmDialog.linked_batches}</strong> linked batch{confirmDialog.linked_batches !== 1 ? 'es' : ''} created for this order</li>
                    )}
                    <li>Dispatch note &amp; dispatch dockets (PDF documents)</li>
                    <li>Any transformations linked to order batches</li>
                  </ul>
                </div>

                <div>
                  <Label htmlFor="purge-reason" className="font-semibold">Reason for deletion *</Label>
                  <Textarea
                    id="purge-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Duplicate entry, incorrect customer, test order..."
                    className="mt-1"
                    rows={3}
                    data-testid="input-purge-reason"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Minimum 5 characters. This will be logged for compliance.</p>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDialog(null)} data-testid="btn-cancel-purge">Cancel</Button>
              <Button
                variant="destructive"
                onClick={handlePurge}
                disabled={purging || reason.trim().length < 5}
                data-testid="btn-confirm-purge"
              >
                {purging ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Purge Order
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
    </div>
  );
}
